import { useCallback, useEffect, useRef, useState } from "react";

import { useRouter } from "@uirouter/react";

import { ONBOARDING_AGENT_NUDGE_DELAY_MS } from "../../OnboardingAgent/constants";
import { onboardingAgentPreferencesService } from "../../onboardingAgentPreferencesService";
import { onboardingAgentTrackingService } from "../../onboardingAgentTrackingService";
import { hasNudgeBeenShownRecently, markNudgeShown } from "../helpers/onboardingAgentNudgeCooldownStorage";

import { useOnboardingAgentNudgeSuggestionsQuery } from "./useOnboardingAgentNudgeSuggestionsQuery";

type UseOnboardingAgentNudgeParams = {
    readonly isPanelOpen: boolean;
    readonly onAsk: (question: string) => void;
};

type UseOnboardingAgentNudgeResult = {
    readonly isVisible: boolean;
    readonly suggestions: readonly string[];
    readonly handleAsk: (question: string) => void;
    readonly handleDismiss: () => void;
};

const EMPTY_SUGGESTIONS: readonly string[] = [];

const canShowNudge = (): boolean => !hasNudgeBeenShownRecently() && onboardingAgentPreferencesService.isFirstOpen();

export const useOnboardingAgentNudge = ({
    isPanelOpen,
    onAsk,
}: UseOnboardingAgentNudgeParams): UseOnboardingAgentNudgeResult => {
    const router = useRouter();
    const routerRef = useRef(router);
    routerRef.current = router;
    const [isVisible, setIsVisible] = useState(false);
    const [shouldFetch, setShouldFetch] = useState(false);
    const timeoutIdRef = useRef<number | null>(null);
    const isPanelOpenRef = useRef(isPanelOpen);
    isPanelOpenRef.current = isPanelOpen;

    const { data, isSuccess } = useOnboardingAgentNudgeSuggestionsQuery(shouldFetch);
    const suggestions = data ?? EMPTY_SUGGESTIONS;

    const clearScheduledNudge = useCallback((): void => {
        if (timeoutIdRef.current !== null) {
            window.clearTimeout(timeoutIdRef.current);
            timeoutIdRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (isPanelOpen) {
            setIsVisible(false);
        }
    }, [isPanelOpen]);

    useEffect(() => {
        const scheduleNudge = (): void => {
            clearScheduledNudge();

            if (!canShowNudge()) {
                return;
            }

            timeoutIdRef.current = window.setTimeout(() => {
                timeoutIdRef.current = null;

                if (isPanelOpenRef.current || !canShowNudge()) {
                    return;
                }

                setShouldFetch(true);
            }, ONBOARDING_AGENT_NUDGE_DELAY_MS);
        };

        const unregister = routerRef.current.transitionService.onSuccess({}, scheduleNudge);

        // `onSuccess` only fires on future transitions; schedule once for the page already open on mount.
        scheduleNudge();

        return () => {
            clearScheduledNudge();
            if (typeof unregister === "function") {
                unregister();
            }
        };
        // Mount-once: re-running would keep rescheduling the timer.
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [clearScheduledNudge]);

    useEffect(() => {
        // Gate on `shouldFetch` so a warm React Query cache cannot show before this mount's delay.
        if (!shouldFetch || !isSuccess || suggestions.length === 0 || isPanelOpenRef.current || !canShowNudge()) {
            return;
        }

        markNudgeShown();
        setIsVisible(true);
        onboardingAgentTrackingService.trackNudgePromotionOpen();
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldFetch, isSuccess, suggestions.length]);

    const handleAsk = useCallback(
        (question: string): void => {
            if (!question.trim()) {
                return;
            }
            // OA15 click tracking fires in the component, where the option's position is known.
            markNudgeShown();
            setIsVisible(false);
            onAsk(question);
        },
        [onAsk],
    );

    const handleDismiss = useCallback((): void => {
        markNudgeShown();
        onboardingAgentTrackingService.trackNudgePromotionClose();
        setIsVisible(false);
    }, []);

    return {
        isVisible,
        suggestions,
        handleAsk,
        handleDismiss,
    };
};
